import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Users, Award, Zap, ChevronRight, BookOpen, Clock, Star } from 'lucide-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { coursesApi } from '../lib/api';
import CourseCard from '../components/ui/CourseCard';
import { GlassButton } from '../components/ui';
import { formatPrice } from '../lib/utils';

interface Course {
  id: string;
  slug: string;
  title: string;
  shortDescription?: string;
  thumbnailUrl?: string;
  price: number;
  currency?: string;
  level: string;
  rating?: number;
  totalReviews: number;
  totalStudents: number;
  totalDuration?: number;
  courseType?: 'RECORDED' | 'LIVE';
  professor: {
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
  category?: {
    name: string;
  };
}

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

// Slider auto-rotate interval in ms
const SLIDER_INTERVAL = 5000;

export default function HomePage() {
  const [featuredCourses, setFeaturedCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const featuresRef = useRef<HTMLElement>(null);

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Limit slider to first 5 courses for the hero
  const sliderCourses = featuredCourses.slice(0, 5);

  // Auto-rotate slider
  useEffect(() => {
    if (sliderCourses.length <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderCourses.length);
    }, SLIDER_INTERVAL);

    return () => clearInterval(interval);
  }, [sliderCourses.length, isPaused]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        // First try to get featured courses
        const featuredResponse = await coursesApi.getFeatured(8);
        if (featuredResponse.data.success && (featuredResponse.data.data as Course[])?.length > 0) {
          setFeaturedCourses(featuredResponse.data.data as Course[]);
        } else {
          // Fallback: get latest courses if no featured courses exist
          const latestResponse = await coursesApi.getAll({ limit: 8 });
          if (latestResponse.data.success) {
            setFeaturedCourses(latestResponse.data.data as Course[] || []);
          }
        }
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        // Try fallback on error too
        try {
          const fallbackResponse = await coursesApi.getAll({ limit: 8 });
          if (fallbackResponse.data.success) {
            setFeaturedCourses(fallbackResponse.data.data as Course[] || []);
          }
        } catch {
          // Both failed, leave empty
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, []);

  return (
    <div className="bg-void">
      {/* ============ HERO SECTION ============ */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Teal-Slate Aurora Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-slate/25 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[350px] bg-slate-light/15 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-slate/15 rounded-full blur-[150px]" />
          {/* Subtle coral accent */}
          <div className="absolute bottom-1/4 right-1/3 w-[200px] h-[200px] bg-coral/10 rounded-full blur-[100px]" />
        </div>

        <div className="relative container-luxury py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
              className="text-center lg:text-left"
            >
              {/* Eyebrow */}
              <motion.div variants={fadeUp} className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full 
                               bg-slate/15 border border-slate/25
                               text-slate-light text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  <span>The Premier Learning Experience</span>
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1 
                variants={fadeUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight"
              >
                Master New Skills{' '}
                <span className="block mt-2">
                  with{' '}
                  <span className="text-gradient">Premium Learning</span>
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p 
                variants={fadeUp}
                className="mt-6 text-lg text-white/50 max-w-xl mx-auto lg:mx-0 leading-relaxed"
              >
                Learn from industry experts, advance your career, and unlock your 
                potential with our curated collection of world-class courses.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                variants={fadeUp}
                className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link to="/courses" className="btn-primary group">
                  <span>Explore Courses</span>
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Link>
                <GlassButton icon={ChevronRight} onClick={scrollToFeatures}>
                  How It Works
                </GlassButton>
              </motion.div>

              {/* Stats */}
              <motion.div 
                variants={fadeUp}
                className="mt-8 sm:mt-12 grid grid-cols-3 gap-4 sm:gap-8"
              >
                {[
                  { value: '10K+', label: 'Active Learners' },
                  { value: '500+', label: 'Expert Instructors' },
                  { value: '1000+', label: 'Premium Courses' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center lg:text-left">
                    <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-gradient">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-[10px] sm:text-xs text-white/40 font-medium uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right Content - Course Slider */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, x: 20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative hidden lg:block"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Main Card - Glass Effect */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-slate rounded-3xl blur-2xl opacity-25" />
                
                {sliderCourses.length > 0 ? (
                  <div className="relative card p-6 overflow-hidden">
                    {/* Slider Container */}
                    <div className="relative">
                      <AnimatePresence mode="wait">
                        {sliderCourses.map((course, index) => (
                          index === currentSlide && (
                            <motion.div
                              key={course.id}
                              initial={{ opacity: 0, x: 50 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -50 }}
                              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            >
                              <Link 
                                to={`/courses/${course.slug}`}
                                className="block group cursor-pointer"
                              >
                                {/* Course Thumbnail */}
                                <div className="aspect-video rounded-xl overflow-hidden relative">
                                  {course.thumbnailUrl ? (
                                    <img 
                                      src={course.thumbnailUrl} 
                                      alt={course.title}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                  ) : (
                                    <div 
                                      className="w-full h-full flex items-center justify-center"
                                      style={{ background: 'linear-gradient(145deg, rgba(42, 74, 91, 0.3) 0%, rgba(15, 16, 20, 0.8) 100%)' }}
                                    >
                                      <BookOpen className="w-12 h-12 text-white/30" />
                                    </div>
                                  )}
                                  
                                  {/* Price badge */}
                                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg 
                                                bg-void/80 backdrop-blur-sm border border-white/10
                                                font-semibold text-sm text-white">
                                    {formatPrice(course.price, course.currency)}
                                  </div>
                                  
                                  {/* Course type badge - only show for LIVE courses */}
                                  {course.courseType === 'LIVE' && (
                                    <div className="absolute top-3 left-3">
                                      <span className="px-2.5 py-1 rounded-lg text-xs font-medium backdrop-blur-sm bg-coral/80 text-white">
                                        Live
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Course Info */}
                                <div className="mt-4 space-y-3">
                                  {/* Category */}
                                  {course.category && (
                                    <span className="text-slate-light text-[10px] font-semibold uppercase tracking-wider">
                                      {course.category.name}
                                    </span>
                                  )}
                                  
                                  <div>
                                    <h3 className="text-white font-semibold text-base line-clamp-1 group-hover:text-slate-light transition-colors">
                                      {course.title}
                                    </h3>
                                    {course.shortDescription && (
                                      <p className="text-white/40 text-sm mt-1 line-clamp-2">
                                        {course.shortDescription}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
                                    {/* Professor */}
                                    <div className="flex items-center gap-2">
                                      {course.professor.avatarUrl ? (
                                        <img 
                                          src={course.professor.avatarUrl} 
                                          alt={course.professor.firstName}
                                          className="w-7 h-7 rounded-full object-cover ring-1 ring-white/10"
                                        />
                                      ) : (
                                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                                             style={{ background: 'linear-gradient(135deg, #2a4a5b 0%, #6c7e8d 100%)' }}>
                                          {course.professor.firstName.charAt(0)}
                                        </div>
                                      )}
                                      <span className="text-white/50 text-sm">
                                        {course.professor.firstName} {course.professor.lastName}
                                      </span>
                                    </div>
                                    
                                    {/* Stats */}
                                    <div className="flex items-center gap-3 text-xs">
                                      {course.rating && course.rating > 0 && (
                                        <div className="flex items-center gap-1">
                                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                          <span className="text-white font-medium">{Number(course.rating).toFixed(1)}</span>
                                        </div>
                                      )}
                                      {course.totalDuration && course.totalDuration > 0 && (
                                        <div className="flex items-center gap-1 text-white/40">
                                          <Clock className="w-3.5 h-3.5" />
                                          <span>{Math.round(course.totalDuration / 60)}h</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </Link>
                            </motion.div>
                          )
                        ))}
                      </AnimatePresence>
                    </div>

                    {/* Slider Navigation Dots */}
                    {sliderCourses.length > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-5">
                        {sliderCourses.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`transition-all duration-300 rounded-full ${
                              index === currentSlide 
                                ? 'w-6 h-2 bg-coral' 
                                : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Fallback placeholder - links to courses page
                  <Link 
                    to="/courses"
                    className="relative card p-6 block group cursor-pointer hover:border-slate/30 transition-all duration-300"
                  >
                    <div className="aspect-video rounded-xl overflow-hidden relative"
                         style={{ background: 'linear-gradient(145deg, rgba(42, 74, 91, 0.3) 0%, rgba(15, 16, 20, 0.8) 100%)' }}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <BookOpen className="w-12 h-12 text-white/30 mx-auto mb-3" />
                          <p className="text-white/50 text-sm">Explore our courses</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-white font-medium">Browse All Courses</p>
                      <p className="text-white/40 text-sm mt-1">Find your perfect learning path</p>
                    </div>
                  </Link>
                )}

                {/* Floating Badge - Certificate */}
                <motion.div
                  animate={{ y: [-6, 6, -6] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-4 -right-4 card px-4 py-2.5"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Award className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <div>
                      <div className="text-[10px] text-white/40">Certified</div>
                      <div className="text-xs text-white font-medium">Get Certificate</div>
                    </div>
                  </div>
                </motion.div>

                {/* Students count floating badge */}
                {sliderCourses.length > 0 && sliderCourses[currentSlide]?.totalStudents > 0 && (
                  <motion.div
                    key={`students-${currentSlide}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1, y: [6, -6, 6] }}
                    transition={{ 
                      opacity: { duration: 0.3 },
                      scale: { duration: 0.3 },
                      y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                    }}
                    className="absolute -bottom-3 -left-3 card px-4 py-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-slate/30 flex items-center justify-center">
                        <Users className="w-3.5 h-3.5 text-slate-light" />
                      </div>
                      <div>
                        <div className="text-[10px] text-white/40">Enrolled</div>
                        <div className="text-xs text-white font-medium">{sliderCourses[currentSlide].totalStudents}+ Students</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES SECTION ============ */}
      <section ref={featuresRef} className="section-padding relative overflow-hidden scroll-mt-20">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        
        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="accent-line mx-auto mb-5" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Why Choose Ulearning?
            </h2>
            <p className="mt-3 text-white/40 max-w-2xl mx-auto">
              Experience the difference with our premium learning platform
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              {
                icon: Award,
                title: 'Expert Instructors',
                description: 'Learn from industry professionals with real-world experience',
              },
              {
                icon: Zap,
                title: 'Live Sessions',
                description: 'Interactive classes with Q&A and real-time collaboration',
              },
              {
                icon: Sparkles,
                title: 'Certificates',
                description: 'Earn certificates recognized by industry leaders',
              },
              {
                icon: Users,
                title: 'Community',
                description: 'Join a network of ambitious professionals',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card card-hover text-center group"
              >
                <div className="icon-slate mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-5 h-5 text-slate-light" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-white/40 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURED COURSES ============ */}
      <section className="section-padding relative">
        <div className="absolute top-1/2 left-0 w-1/2 h-[400px] bg-slate/[0.08] blur-[120px] rounded-full" />
        
        <div className="container-luxury relative">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="accent-line mb-5" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Featured Courses
              </h2>
              <p className="mt-2 text-white/40">
                Hand-curated courses to accelerate your journey
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/courses" className="btn-secondary group">
                <span>View All</span>
                <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card">
                  <div className="skeleton aspect-video rounded-xl" />
                  <div className="skeleton h-3 mt-4 w-1/3 rounded" />
                  <div className="skeleton h-5 mt-2 rounded" />
                  <div className="skeleton h-3 mt-2 w-2/3 rounded" />
                </div>
              ))}
            </div>
          ) : featuredCourses.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {featuredCourses.map((course, index) => (
                <CourseCard key={course.id} course={course} index={index} />
              ))}
              {/* Fill empty grid space with "View More" card */}
              {featuredCourses.length % 4 !== 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: featuredCourses.length * 0.06, duration: 0.4 }}
                >
                  <Link
                    to="/courses"
                    className="card card-hover h-full flex flex-col items-center justify-center text-center group min-h-[280px]"
                  >
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4
                                  group-hover:scale-110 transition-transform"
                         style={{ background: 'linear-gradient(135deg, rgba(220, 70, 74, 0.15) 0%, rgba(255, 229, 226, 0.08) 100%)', border: '1px solid rgba(220, 70, 74, 0.2)' }}>
                      <ArrowRight className="w-6 h-6 text-coral" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">Explore More</h3>
                    <p className="text-white/40 text-sm">
                      Discover all our courses
                    </p>
                  </Link>
                </motion.div>
              )}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-white/40">No featured courses yet. Check back soon!</p>
            </motion.div>
          )}

          <div className="sm:hidden mt-8 text-center">
            <Link to="/courses" className="btn-secondary">
              View All Courses
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-mesh-strong" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate/30 to-transparent" />

        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="accent-line mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              Ready to Start Your{' '}
              <span className="text-gradient">Learning Journey</span>?
            </h2>
            <p className="mt-4 text-white/50 text-lg">
              Join thousands of learners already advancing their careers.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="btn-primary">
                Get Started Free
              </Link>
              <Link to="/courses" className="btn-secondary">
                Browse Courses
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
